import { TGoal, activeGoals } from './goalTypes'

/*
 * Named `noGoalNudgeRules`, not `noGoalNudge`, because NoGoalNudge.tsx sits beside it: two modules
 * in one directory differing only in case resolve to whichever the filesystem feels like on macOS
 * and Windows, which took the whole Goals page down locally while reading fine in CI. Keep the
 * names distinct — recurringGoalHookUtils.ts / RecurringGoalHooks.tsx is the same split.
 */

/**
 * Why an account is being nudged about goals, or null for "say nothing".
 *
 * 'paused' counts as having nothing to aim at, not as having a goal: recomputeGoal skips a paused
 * goal (backend/api/goal_progress.go), GoalSelector shows it but won't let a task link to it
 * (molecules/GoalSelector.tsx), and GoalDetails' only call to action on one is Resume.
 */
export type TNoGoalReason = 'none' | 'paused'

/**
 * One home for the copy, shared by the overview nudge and the Goals page's own empty state so the
 * two can't drift. It carries the ramp the product wants: one thing a week, and realistic before
 * ambitious.
 */
export const NO_GOAL_COPY: Record<TNoGoalReason, { title: string; body: string; action: string }> = {
    none: {
        title: 'No goals yet',
        body: 'Start with one thing a week — something realistic you can keep to.',
        action: 'Set a goal',
    },
    paused: {
        title: 'Your goals are paused',
        body: 'Restart one, or set a smaller weekly goal you can keep to.',
        action: 'Review goals',
    },
}

interface NoGoalState {
    goals: TGoal[]
    areGoalsLoading: boolean
    /** The goals request failed, so `goals` is unknown rather than empty. */
    areGoalsUnavailable?: boolean
    isDismissed?: boolean
}

/**
 * The single owner of "does this account need a goal nudge". Silent unless the account's goal state
 * is actually known: every account looks goal-less on its first render, and a failed request leaves
 * the caller holding an empty list too — telling someone who has goals to set one, because a fetch
 * broke, is worse than saying nothing.
 */
export const noGoalReason = ({
    goals,
    areGoalsLoading,
    areGoalsUnavailable = false,
    isDismissed = false,
}: NoGoalState): TNoGoalReason | null => {
    if (areGoalsLoading || areGoalsUnavailable || isDismissed) return null
    // Archived goals never satisfy the nudge: an account whose only goal is archived has nothing to
    // aim at, the same as one whose only goal is paused.
    const live = activeGoals(goals)
    if (live.length === 0) return 'none'
    if (live.every((goal) => goal.status === 'paused')) return 'paused'
    return null
}
